import { useEffect, type RefObject } from 'react';

/** Atributo das áreas roláveis das colunas — o autoscroll vertical procura por ele. */
export const KANBAN_SCROLL_ATTR = 'data-kanban-scroll';

const DEAD_ZONE = 0.08; // fração da meia-largura, em volta do centro, que não rola
const MAX_X = 1500; // px/s com o cursor colado na borda lateral
const MAX_Y = 1100; // px/s com o cursor colado no topo/rodapé da coluna
const EDGE_Y = 110; // altura da faixa de rolagem vertical dentro da coluna

/** 0 → 1, em curva: devagar perto do início da zona, rápido perto da borda. */
const ease = (t: number) => Math.min(1, Math.max(0, t)) ** 2;

/**
 * Rolagem automática enquanto um card (ou coluna) é arrastado, como no
 * DataCrazy: basta o cursor estar na metade direita ou esquerda do quadro —
 * em qualquer altura — para ele rolar para aquele lado, mais rápido quanto mais
 * perto da borda. Dentro de uma coluna com rolagem própria, chegar perto do
 * topo ou do rodapé rola a coluna.
 *
 * O arrastar é o nativo do HTML5, que não dispara mousemove; a posição vem do
 * `dragover` do documento e um requestAnimationFrame aplica a rolagem.
 */
export function useDragAutoScroll(boardRef: RefObject<HTMLElement | null>, active: boolean) {
  useEffect(() => {
    if (!active) return;
    let pointer: { x: number; y: number } | null = null;
    let frame = 0;
    let last = performance.now();
    // scrollLeft/scrollTop arredondam: o resto fracionário fica guardado aqui
    let carryX = 0;
    let carryY = 0;

    const onOver = (event: DragEvent) => {
      pointer = { x: event.clientX, y: event.clientY };
    };
    const onStop = () => {
      pointer = null;
    };
    // cursor saiu da janela: para de rolar até ele voltar
    const onLeave = (event: DragEvent) => {
      if (!event.relatedTarget) pointer = null;
    };

    const tick = (now: number) => {
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      const board = boardRef.current;
      if (board && pointer) {
        const rect = board.getBoundingClientRect();
        const half = rect.width / 2;
        const offset = (pointer.x - (rect.left + half)) / half;
        const strength = ease((Math.abs(offset) - DEAD_ZONE) / (1 - DEAD_ZONE));
        carryX += Math.sign(offset) * strength * MAX_X * dt;
        const stepX = Math.trunc(carryX);
        if (stepX !== 0) {
          board.scrollLeft += stepX;
          carryX -= stepX;
        }

        // a coluna que está sob o X do cursor, mesmo com ele acima ou abaixo dela
        const columns = board.querySelectorAll<HTMLElement>(`[${KANBAN_SCROLL_ATTR}]`);
        const column = Array.from(columns).find((element) => {
          const box = element.getBoundingClientRect();
          return pointer!.x >= box.left && pointer!.x <= box.right;
        });
        if (column && column.scrollHeight > column.clientHeight) {
          const box = column.getBoundingClientRect();
          const edge = Math.min(EDGE_Y, box.height / 3);
          const down = ease((pointer.y - (box.bottom - edge)) / edge);
          const up = ease((box.top + edge - pointer.y) / edge);
          carryY += (down - up) * MAX_Y * dt;
          const stepY = Math.trunc(carryY);
          if (stepY !== 0) {
            column.scrollTop += stepY;
            carryY -= stepY;
          }
        } else {
          carryY = 0;
        }
      }
      frame = requestAnimationFrame(tick);
    };

    document.addEventListener('dragover', onOver);
    document.addEventListener('dragleave', onLeave);
    document.addEventListener('drop', onStop);
    document.addEventListener('dragend', onStop);
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('dragover', onOver);
      document.removeEventListener('dragleave', onLeave);
      document.removeEventListener('drop', onStop);
      document.removeEventListener('dragend', onStop);
    };
  }, [boardRef, active]);
}
